import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import type { CourseWithFullDetails } from '@/lib/types';
import { sortFocusSkillsByOrder } from '@/lib/utils';

const schoolId = import.meta.env.VITE_SCHOOL_ID;

export function useGetCurrentCourses(courseId?: string) {
	return useQuery<CourseWithFullDetails | null>({
		queryKey: ['get-current-course', courseId, schoolId],
		queryFn: async () => {
			if (!courseId) return null;

			const { data, error } = await supabase
				.from('courses')
				.select(
					`
					course_name,
					course_id,
					course_description,
					course_resource_info,
					course_prereq,
					course_extras,
					course_discipline,
					course_division,
					submission_id,
					course_draft,
					is_archived,
					working_copy,
					course_lesson_plans,
					is_saved_draft,
					last_updated,
					submission:submissions!courses_submission_id_fkey (
						admin_approval,
						dept_approval
					),
					courses_disciplines (
						discipline:disciplines (
							name
						)
					),
					courses_users (
						user_id,
						course_id,
						user:users (
							user_first,
							user_last,
							user_id,
							user_email,
							user_dept_chair
						)
					),
					courses_outcomes (
						courses_outcomes_id,
						outcome:outcomes (
							outcome_order,
							outcome_text,
							outcome_id
						)
					),
					courses_resources (
						course_resources_id,
						resource:resources (
							resource_author,
							resource_detail,
							resource_isbn,
							resource_id,
							resource_title,
							resource_type
						)
					),
					courses_focuses (
						courses_focuses_id,
						focus:focuses (
							focus_title,
							focus_type,
							focus_order,
							focus_id,
							focuses_skills (
								focuses_skills_id,
								skill_order,
								skill:skills (
									skill_id,
									skill_text,
									skill_type
								)
							)
						)
					)
				`
				)
				.eq('course_id', courseId)
				.eq('school_id', schoolId)
				.maybeSingle();

			if (error) throw error;

			if (!data) return null;

			sortFocusSkillsByOrder(data.courses_focuses);

			// Fetch dept_chair emails for users that have a dept_chair
			// Note: user_dept_chair stores Firebase UIDs, so we query by user_firebase_id
			const deptChairIds =
				data.courses_users
					?.map((cu: any) => cu.user?.user_dept_chair)
					.filter((id: string | null | undefined): id is string => !!id) ?? [];

			let deptChairEmails: Record<string, string | null> = {};
			if (deptChairIds.length > 0) {
				const { data: deptChairs } = await supabase
					.from('users')
					.select('user_firebase_id, user_email')
					.in('user_firebase_id', deptChairIds);

				deptChairEmails =
					deptChairs?.reduce(
						(acc, dc) => {
							acc[dc.user_firebase_id] = dc.user_email;
							return acc;
						},
						{} as Record<string, string | null>
					) ?? {};
			}

			// Transform the data to match the expected shape
			const transformedData = {
				...data,
				// Rename courses_disciplines to courses_discipline (singular)
				courses_discipline: data.courses_disciplines ?? null,
				// Transform courses_users to include dept_chair object structure
				courses_users: data.courses_users?.map((cu: any) => ({
					...cu,
					user: cu.user
						? {
								...cu.user,
								// Transform user_dept_chair string to dept_chair object
								dept_chair: cu.user.user_dept_chair
									? {
											user_email: deptChairEmails[cu.user.user_dept_chair] ?? null,
											user_id: cu.user.user_dept_chair,
										}
									: null,
							}
						: null,
				})),
			};

			return transformedData as unknown as CourseWithFullDetails | null;
		},
		staleTime: Infinity,
		refetchOnMount: 'always',
		enabled: !!courseId,
	});
}
